import React from "react";
import styled from "styled-components";
import { Container } from "@mui/material";
import SEO from "../components/SEO";
import { settings } from "../data/settings";
import GuideLeadForm from "../components/GuideLeadForm";
import SeoInternalLinks from "../components/SeoInternalLinks";

const Title = styled.h1`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: var(--my-color3);
`;

const SubTitle = styled.h2`
  font-size: 1.4rem;
  margin-top: 2rem;
  margin-bottom: 0.7rem;
  color: var(--my-color3);
`;

const Paragraph = styled.p`
  line-height: 1.7;
  margin-bottom: 1rem;
`;

const List = styled.ul`
  margin: 0 0 1rem 1.2rem;
  padding: 0;
  line-height: 1.7;
`;

const CTA = styled.a`
  display: inline-block;
  margin-top: 0.6rem;
  margin-right: 0.75rem;
  text-decoration: none;
  background: var(--my-color3);
  color: white;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 700;
  text-transform: uppercase;
`;

export default function DeveloppeurWebFreelanceParisPage() {
  return (
    <Container
      sx={{ py: 4 }}
      style={{ maxWidth: "900px", marginTop: settings.navbar.height }}
    >
      <SEO
        title="Développeur web freelance Paris | DigiBuddy"
        description="Développeur web freelance basé à Paris : création de site web sur mesure, refonte, SEO et accompagnement. Intervention à Paris, en France et en Europe."
        keywords="développeur web freelance paris, création site internet paris, freelance web paris, refonte site web paris, développeur freelance france, développeur web europe"
        url="https://www.digibuddy.fr/developpeur-web-freelance-paris"
        canonical="https://www.digibuddy.fr/developpeur-web-freelance-paris"
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: "Développeur web freelance à Paris",
            areaServed: ["Paris", "France", "Europe"],
            provider: {
              "@type": "Organization",
              name: "DigiBuddy",
              url: "https://www.digibuddy.fr",
            },
            serviceType: "Création de site web sur mesure",
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Intervenez-vous uniquement à Paris ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Paris est la zone prioritaire, mais nous accompagnons aussi des clients dans toute la France et en Europe.",
                },
              },
              {
                "@type": "Question",
                name: "Travaillez-vous avec des villes de province ?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Oui, notamment Lyon, Marseille, Lille, Bordeaux, Nantes, Toulouse, Rennes, Strasbourg, Montpellier et Nice.",
                },
              },
            ],
          },
        ]}
      />

      <Title>Développeur web freelance à Paris (France & Europe)</Title>
      <Paragraph>
        DigiBuddy accompagne en priorité les entreprises à Paris pour la création
        de sites web sur mesure, la refonte et l’optimisation SEO. Les projets
        sont également menés partout en France et en Europe en mode distanciel ou
        hybride.
      </Paragraph>

      <SubTitle>Zone principale : Paris</SubTitle>
      <List>
        <li>Cadrage rapide avec interlocuteur unique</li>
        <li>Livraison orientée conversion et prise de contact</li>
        <li>Accompagnement continu après mise en ligne</li>
      </List>

      <SubTitle>Couverture France & Europe</SubTitle>
      <Paragraph>
        Au-delà de Paris, nous traitons des projets dans les grandes villes de
        province et à l’international francophone. Exemples fréquents : Lyon,
        Marseille, Lille, Bordeaux, Nantes, Toulouse, Rennes, Strasbourg,
        Montpellier et Nice.
      </Paragraph>

      <SubTitle>Types de missions</SubTitle>
      <List>
        <li>Création de site web sur mesure</li>
        <li>Refonte de site existant</li>
        <li>Landing pages SEO / conversion</li>
        <li>Maintenance et évolutions techniques</li>
      </List>

      <SubTitle>FAQ</SubTitle>
      <Paragraph>
        <strong>Intervenez-vous uniquement à Paris ?</strong>
        <br />
        Non. Paris est prioritaire, mais l’accompagnement est aussi possible en
        France et en Europe.
      </Paragraph>
      <Paragraph>
        <strong>Travaillez-vous avec des villes de province ?</strong>
        <br />
        Oui : Lyon, Marseille, Lille, Bordeaux, Nantes, Toulouse, Rennes,
        Strasbourg, Montpellier, Nice, selon les besoins.
      </Paragraph>

      <CTA href="/#contact">Parler de mon projet</CTA>
      <CTA href="/creation-site-web-sur-mesure" style={{ background: "var(--my-color2)" }}>
        Création de site web
      </CTA>

      <SeoInternalLinks
        links={[
          {
            to: "/creation-site-web-sur-mesure",
            label: "Création de site web sur mesure",
          },
          {
            to: "/combien-coute-site-web-sur-mesure",
            label: "Combien coûte un site web sur mesure ?",
          },
          {
            to: "/freelance-ou-agence-pour-creer-son-site",
            label: "Freelance ou agence ?",
          },
        ]}
      />

      <GuideLeadForm source="local-paris-page" />
    </Container>
  );
}
